import React, { useEffect, useState } from 'react';
import { fetchApi } from '../../services/api';
import LoadingPanel from '../../components/LoadingPanel';
import { Checkbox, TextArea } from '@progress/kendo-react-inputs';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from '@progress/kendo-react-buttons';
import { JsonResponse } from 'TypeGen/json-response';
import { DateTimePicker } from '@progress/kendo-react-dateinputs';
import dayjs from 'dayjs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useHistory } from 'react-router-dom';
import { FailureReasonViewModel } from 'TypeGen/ServiceExceptions/failure-reason-view-model';

type CreateRequest = {
  DateTime: Date | null;
  Note: string;
  FailureReasonID: number;
  CustomerNotified: boolean;
  SelfAssign: boolean;
  ForwardToOrderNote: boolean;
  ForwardToBillingNote: boolean;
};

type Props = {
  OrderID: number;
  CloseDialog: () => void;
};

type GetCreateServiceException = {
  OrderNumber: number;
  DateTime: string;
  FailureReasons: FailureReasonViewModel[];
}

const CreateServiceException: React.FC<Props> = ({ OrderID, CloseDialog }) => {
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  const [request, setRequest] = useState<CreateRequest>({ Note: '', DateTime: null, FailureReasonID: 0, CustomerNotified: false, SelfAssign: false, ForwardToOrderNote: true, ForwardToBillingNote: false });
  const [failureReasons, setFailureReasons] = useState<FailureReasonViewModel[]>([]);

  const updateRequest = (value: Partial<CreateRequest>) => {
    setRequest((prev) => ({ ...prev, ...value }));
  };

  useEffect(() => {
    fetchApi(`/api/Order/CreateServiceException/${OrderID}`)
      .then((response: GetCreateServiceException) => {
        updateRequest({ DateTime: dayjs.utc(response.DateTime).toDate() });
        setFailureReasons(response.FailureReasons);
      });
  }, [OrderID]);

  const save = () => {

    if (request.FailureReasonID === 0) {
      alert('Please select a failure reason');
      return;
    }

    if (!request.Note) {
      alert('Please enter a note');
      return;
    }

    setLoading(true);
    const payload = {
      ...request,
      Notes: [
        {
          Note: request.Note,
          ForwardToOrderNote: request.ForwardToOrderNote,
          ForwardToBillingNote: request.ForwardToBillingNote,
          CustomerNotified: request.CustomerNotified,
        }
      ]
    }
    fetchApi(`/api/Order/AddServiceException/${OrderID}`, payload, 'POST')
      .then((response: JsonResponse) => {
        if (response.Success) {
          const link = response.Links.find(l => l.Name === 'OrderServiceException');
          if (link) history.push(link.Link);
          CloseDialog();
        } else {
          alert(response.ErrorMessage);
        }
      })
      .catch((e) => {
        if (!e?.status) alert('Unable to create service exception!');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <React.Fragment>
        <div className="k-overlay" style={{ zIndex: 10002, opacity: 0.5 }} />
        <LoadingPanel />
      </React.Fragment>
    );
  }

  return (
    <Dialog title="Create Service Exception" onClose={CloseDialog} className="dialog-w525">
      <DropDownList
        data={failureReasons}
        textField="Name"
        dataItemKey="ID"
        groupField="GroupName"
        defaultValue={{ ID: 0, Name: 'Select Failure Reason', GroupName: '' }}
        value={failureReasons.find(f => f.ID === request.FailureReasonID)}
        onChange={(e) => updateRequest({ FailureReasonID: e.value.ID })}
      />
      <DateTimePicker
        required
        width="100%"
        format="MM/dd/yyyy HH:mm"
        formatPlaceholder="formatPattern"
        className='mt-2'
        value={request.DateTime}
        onChange={(e) => updateRequest({ DateTime: e.value })}
      />
      <TextArea
        required
        placeholder="Please describe the service exception"
        className='my-2'
        value={request.Note}
        onChange={(e) => updateRequest({ Note: e.value })}
      />
      <div className="mt-2">
        <Checkbox
          label="FW: Order General Note"
          checked={request.ForwardToOrderNote}
          onChange={(e) => updateRequest({ ForwardToOrderNote: e.value })}
        />
      </div>
      <div className="mt-2">
        <Checkbox
          label="FW: Billing Note"
          checked={request.ForwardToBillingNote}
          onChange={(e) => updateRequest({ ForwardToBillingNote: e.value })}
        />
      </div>
      <div className="mt-2">
        <Checkbox
          label="Customer Notified"
          checked={request.CustomerNotified}
          onChange={(e) => updateRequest({ CustomerNotified: e.value })}
        />
      </div>
      <div className="mt-2">
        <Checkbox
          label="Self Assign"
          checked={request.SelfAssign}
          onChange={(e) => updateRequest({ SelfAssign: e.value })}
        />
      </div>

      <DialogActionsBar layout="end">
        <Button onClick={CloseDialog}>Cancel</Button>
        <Button themeColor="success" onClick={save} disabled={loading}>Create</Button>
      </DialogActionsBar>
    </Dialog>
  );
};

export default CreateServiceException;
